// src/utils/context/sideNavigationContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export const SideNavigationContext = createContext()

export const useSideNavigationContext = () => {
  const ctx = useContext(SideNavigationContext)
  if (!ctx) throw new Error('useSideNavigationContext must be used within a SideNavigationContextProvider')
  return ctx
}

const escapeCss = (id) => {
  if (typeof id !== 'string') return id
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(id)
  return id.replace(/([ #;.?+*~'"!:^$[\]()=>|/@])/g, '\\$1')
}

const findById = (id) => {
  const esc = escapeCss(id)
  let el =
    document.getElementById(id) ||
    document.querySelector(`[id="${esc}"]`) ||
    document.querySelector(`#${esc}`)
  if (el) return el
  const lower = id.toLowerCase()
  const all = document.querySelectorAll('[id]')
  for (let i = 0; i < all.length; i++) {
    if (all[i].id.toLowerCase() === lower) return all[i]
  }
  return null
}

const SCROLL_ANIM_MS = 250
const SCROLL_IDLE_MS = 250
const ACTIVATION_EPSILON = 8

const SideNavigationContextProvider = ({ children }) => {
  const [highlightedNavItem, setHighlightedNavItem] = useState(0)
  const [headerOffset, setHeaderOffset] = useState(96)

  const tracksRef = useRef([])
  const idToIndexRef = useRef(new Map())
  const indexToIdRef = useRef([])
  const refsByIndex = useRef([])

  const modeRef = useRef({ type: 'idle', id: null, index: null })
  const idleTimerRef = useRef(null)

  const setTracks = useCallback((tracks) => {
    const clean = Array.isArray(tracks) ? tracks.filter(Boolean) : []
    tracksRef.current = clean
    const map = new Map()
    const idxToId = []
    clean.forEach(({ id, index }) => {
      if (typeof index === 'number' && id) {
        map.set(id, index)
        idxToId[index] = id
      }
    })
    idToIndexRef.current = map
    indexToIdRef.current = idxToId
  }, [])

  const setTrackedSections = useCallback((tracks) => {
    const clean = Array.isArray(tracks) ? tracks.filter(Boolean) : []
    tracksRef.current = clean
    const map = new Map()
    const idxToId = []
    clean.forEach(({ id, index }) => {
      if (typeof index === 'number' && id) {
        map.set(id, index)
        idxToId[index] = id
      }
    })
    idToIndexRef.current = map
    indexToIdRef.current = idxToId
  }, [])

  const setTrackedIds = useCallback((idsOrTracks) => {
    if (Array.isArray(idsOrTracks) && idsOrTracks.length && typeof idsOrTracks[0] === 'object') {
      const clean = idsOrTracks.filter(Boolean)
      tracksRef.current = clean
      const map = new Map()
      const idxToId = []
      clean.forEach(({ id, index }, i) => {
        const idx = typeof index === 'number' ? index : i
        if (id) {
          map.set(id, idx)
          idxToId[idx] = id
        }
      })
      idToIndexRef.current = map
      indexToIdRef.current = idxToId
    } else {
      const ids = (idsOrTracks || []).filter(Boolean)
      tracksRef.current = ids.map((id, i) => ({ id, index: i }))
      const map = new Map()
      const idxToId = []
      ids.forEach((id, i) => {
        map.set(id, i)
        idxToId[i] = id
      })
      idToIndexRef.current = map
      indexToIdRef.current = idxToId
    }
  }, [])

  const createSectionRef = useCallback((index) => {
    if (!refsByIndex.current[index]) refsByIndex.current[index] = React.createRef()
    return refsByIndex.current[index]
  }, [])

  const anchorAbsTop = useCallback((index) => {
    const id = indexToIdRef.current[index]
    const idEl = id ? findById(id) : null
    if (idEl) {
      const rect = idEl.getBoundingClientRect()
      return rect.top + (typeof window !== 'undefined' ? window.scrollY : 0)
    }
    const refEl = refsByIndex.current[index]?.current || null
    if (refEl) {
      const rect = refEl.getBoundingClientRect()
      return rect.top + (typeof window !== 'undefined' ? window.scrollY : 0)
    }
    return null
  }, [])

  const computeActive = useCallback(() => {
    if (typeof window === 'undefined') return
    if (modeRef.current.type === 'anchor') return

    const tracks = tracksRef.current
    if (!tracks.length) return

    const thresholdY =
      (typeof window !== 'undefined' ? window.scrollY : 0) + headerOffset + ACTIVATION_EPSILON

    const positions = []
    for (let i = 0; i < tracks.length; i++) {
      const idx = tracks[i].index
      const absTop = anchorAbsTop(idx)
      if (absTop === null || absTop === undefined || Number.isNaN(absTop)) continue
      positions.push({ index: idx, absTop })
    }
    if (!positions.length) return

    positions.sort((a, b) => a.index - b.index)

    let activeIndex = positions[0].index
    if (thresholdY <= positions[0].absTop) {
      activeIndex = positions[0].index
    } else if (thresholdY >= positions[positions.length - 1].absTop) {
      activeIndex = positions[positions.length - 1].index
    } else {
      for (let i = positions.length - 1; i >= 0; i--) {
        if (positions[i].absTop <= thresholdY) {
          activeIndex = positions[i].index
          break
        }
      }
    }

    if (activeIndex !== highlightedNavItem) setHighlightedNavItem(activeIndex)
  }, [anchorAbsTop, headerOffset, highlightedNavItem])

  useEffect(() => {
    if (typeof window === 'undefined') return
    let ticking = false
    const onFrame = () => {
      computeActive()
      ticking = false
    }
    const onScroll = () => {
      if (modeRef.current.type === 'anchor') {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
        idleTimerRef.current = setTimeout(() => {
          modeRef.current = { type: 'idle', id: null, index: null }
          computeActive()
        }, SCROLL_IDLE_MS)
        return
      }
      if (!ticking) {
        window.requestAnimationFrame(onFrame)
        ticking = true
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    window.addEventListener('hashchange', onScroll)
    window.addEventListener('load', onScroll)
    onScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('hashchange', onScroll)
      window.removeEventListener('load', onScroll)
    }
  }, [computeActive])

  const jumpToId = useCallback((id, onDone) => {
    if (typeof window === 'undefined') return null
    const el = findById(id)
    if (!el) return null
    const yTarget =
      el.getBoundingClientRect().top + window.scrollY - headerOffset
    const start = window.pageYOffset
    const distance = yTarget - start
    const startTime = performance.now()
    const step = (t) => {
      const elapsed = t - startTime
      const k = Math.min(elapsed / SCROLL_ANIM_MS, 1)
      const newY = start + distance * k
      window.scrollTo(0, newY)
      if (k < 1) {
        window.requestAnimationFrame(step)
      } else {
        onDone && onDone()
      }
    }
    window.requestAnimationFrame(step)
    return yTarget
  }, [headerOffset])

  const beginAnchorScroll = useCallback((id, index) => {
    modeRef.current = { type: 'anchor', id, index }
    if (typeof index === 'number') setHighlightedNavItem(index)
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const endAnchorScrollSoon = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    idleTimerRef.current = setTimeout(() => {
      modeRef.current = { type: 'idle', id: null, index: null }
      computeActive()
    }, SCROLL_IDLE_MS)
  }, [computeActive])

  const value = useMemo(
    () => ({
      highlightedNavItem,
      setHighlightedNavItem,
      createSectionRef,
      setHeaderOffset,
      setTracks,
      setTrackedSections,
      setTrackedIds,
      jumpToId,
      beginAnchorScroll,
      endAnchorScrollSoon,
    }),
    [
      highlightedNavItem,
      setHeaderOffset,
      setTracks,
      setTrackedSections,
      setTrackedIds,
      jumpToId,
      beginAnchorScroll,
      endAnchorScrollSoon,
      createSectionRef,
    ]
  )

  return (
    <SideNavigationContext.Provider value={value}>
      {children}
    </SideNavigationContext.Provider>
  )
}

export default SideNavigationContextProvider
